import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import type { ChatGenerationMode } from '../../types/chat';

// チャットとメッセージの型定義
export interface Message {
  id: number;
  chat_id: number;
  user_id: number;
  agent_id?: number;
  content: string;
  created_at: string;
}

export interface Chat {
  id: number;
  title: string;
  user_id: number;
  created_at: string;
  updated_at: string;
  messages: Message[];
}

// 選択中の商品の型定義
export interface SelectedProduct {
  id: number;
  name: string;
  // 商品選択モーダルの「誰に、何を伝えるか」で選んだセグメント／訴求（任意）。
  // 商品と一緒に解除・送信後クリアされるよう、同じオブジェクトに持たせる。
  segmentAppeal?: SelectedProductSegmentAppeal | null;
  // 商品のメイン画像（登録があれば）。生成時の参照画像として後続で送る想定。
  primaryImage?: SelectedProductImage | null;
}

export interface SelectedProductSegmentAppeal {
  segmentId: string;
  segmentName: string;
  appealId: string;
  appealName: string;
  appealDirection: string;
}

export interface SelectedProductImage {
  id: number | string;
  url: string;
}

// チャット状態の型定義
interface ChatState {
  chats: Chat[];
  currentChat: Chat | null;
  isLoading: boolean;
  error: string | null;
  sendingMessage: boolean;
  selectedProduct: SelectedProduct | null;
  /** 入力欄の画像／動画モード。入力欄は新規チャットの初回送信で作り直されるため、ここで保持する */
  generationMode: ChatGenerationMode | null;
}

// 初期状態
const initialState: ChatState = {
  chats: [],
  currentChat: null,
  isLoading: false,
  error: null,
  sendingMessage: false,
  selectedProduct: null,
  generationMode: null,
};

// チャット一覧取得アクション
export const fetchChats = createAsyncThunk(
  'chat/fetchChats',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const token = state.auth.token;

    if (!token) {
      return rejectWithValue('認証トークンがありません');
    }

    try {
      const response = await fetch('/api/chats', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || 'チャット一覧の取得に失敗しました');
      }

      return data.data.chats;
    } catch (error) {
      return rejectWithValue('サーバーとの通信に失敗しました');
    }
  }
);

// チャット詳細取得アクション
export const fetchChatById = createAsyncThunk(
  'chat/fetchChatById',
  async (chatId: number, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const token = state.auth.token;

    if (!token) {
      return rejectWithValue('認証トークンがありません');
    }

    try {
      const response = await fetch(`/api/chats/${chatId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || 'チャットの取得に失敗しました');
      }

      return data.data.chat;
    } catch (error) {
      return rejectWithValue('サーバーとの通信に失敗しました');
    }
  }
);

// 新規チャット作成アクション
export const createChat = createAsyncThunk(
  'chat/createChat',
  async (title: string, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const token = state.auth.token;

    if (!token) {
      return rejectWithValue('認証トークンがありません');
    }

    try {
      const response = await fetch('/api/chats', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title }),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || 'チャットの作成に失敗しました');
      }

      return data.data.chat;
    } catch (error) {
      return rejectWithValue('サーバーとの通信に失敗しました');
    }
  }
);

// メッセージ送信アクション
export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (
    { chatId, content, agentId }: { chatId: number; content: string; agentId?: number },
    { getState, rejectWithValue }
  ) => {
    const state = getState() as RootState;
    const token = state.auth.token;

    if (!token) {
      return rejectWithValue('認証トークンがありません');
    }

    try {
      const messageData = {
        chat_id: chatId,
        content,
        ...(agentId && { agent_id: agentId }),
      };

      const response = await fetch('/api/chat/messages', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messageData),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || 'メッセージの送信に失敗しました');
      }

      return data.data.message;
    } catch (error) {
      return rejectWithValue('サーバーとの通信に失敗しました');
    }
  }
);

// チャットスライス
const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    clearChatError: (state) => {
      state.error = null;
    },
    clearCurrentChat: (state) => {
      state.currentChat = null;
    },
    setSelectedProduct: (state, action: PayloadAction<SelectedProduct | null>) => {
      state.selectedProduct = action.payload;
    },
    clearSelectedProduct: (state) => {
      state.selectedProduct = null;
    },
    setGenerationMode: (state, action: PayloadAction<ChatGenerationMode | null>) => {
      state.generationMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    // チャット一覧取得
    builder
      .addCase(fetchChats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchChats.fulfilled, (state, action: PayloadAction<Chat[]>) => {
        state.isLoading = false;
        state.chats = action.payload;
        state.error = null;
      })
      .addCase(fetchChats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // チャット詳細取得
    builder
      .addCase(fetchChatById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchChatById.fulfilled, (state, action: PayloadAction<Chat>) => {
        state.isLoading = false;
        state.currentChat = action.payload;
        
        // チャット一覧も更新
        const index = state.chats.findIndex(chat => chat.id === action.payload.id);
        if (index !== -1) {
          state.chats[index] = action.payload;
        }
        
        state.error = null;
      })
      .addCase(fetchChatById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 新規チャット作成
    builder
      .addCase(createChat.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createChat.fulfilled, (state, action: PayloadAction<Chat>) => {
        state.isLoading = false;
        state.chats.unshift(action.payload); // 新しいチャットを先頭に追加
        state.currentChat = action.payload;
        state.error = null;
      })
      .addCase(createChat.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // メッセージ送信
    builder
      .addCase(sendMessage.pending, (state) => {
        state.sendingMessage = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action: PayloadAction<Message>) => {
        state.sendingMessage = false;
        
        // 現在のチャットにメッセージを追加
        if (state.currentChat && state.currentChat.id === action.payload.chat_id) {
          state.currentChat.messages.push(action.payload);
        }
        
        // チャット一覧も更新
        const chatIndex = state.chats.findIndex(chat => chat.id === action.payload.chat_id);
        if (chatIndex !== -1) {
          // チャットのメッセージが配列として含まれている場合はそこに追加
          if (state.chats[chatIndex].messages) {
            state.chats[chatIndex].messages.push(action.payload);
          } else {
            // メッセージが含まれていない場合は空配列を作成して追加
            state.chats[chatIndex].messages = [action.payload];
          }
          
          // タイトルが「新規チャット」の場合は最初のメッセージを基にタイトルを更新
          if (state.chats[chatIndex].title === '新規チャット' && state.chats[chatIndex].messages.length === 1) {
            // メッセージの最初の20文字をタイトルとして使用
            const newTitle = action.payload.content.substring(0, 20) + (action.payload.content.length > 20 ? '...' : '');
            state.chats[chatIndex].title = newTitle;
            
            // 現在のチャットのタイトルも更新
            if (state.currentChat && state.currentChat.id === action.payload.chat_id) {
              state.currentChat.title = newTitle;
            }
          }
          
          // 更新日時を更新
          state.chats[chatIndex].updated_at = new Date().toISOString();
        }
        
        state.error = null;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sendingMessage = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearChatError, clearCurrentChat, setSelectedProduct, clearSelectedProduct, setGenerationMode } = chatSlice.actions;

export default chatSlice.reducer;
